import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type { BankAccount } from "@/hooks/use-bank-accounts";

import {
  PAGE_SIZE,
  fmtMoney,
  rangeLabel,
  ScoreBadge,
  LoadingBlock,
  EmptyBlock,
  ErrorBlock,
  MatchKindBadge,
  TableShell,
} from "./matching-ui";

/** Date format: "3 Feb, 2026" (no leading zero, no time) */
function fmtShortDate(d: any): string {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";

  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).formatToParts(dt);

  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const mon = parts.find((p) => p.type === "month")?.value ?? "";
  const yr = parts.find((p) => p.type === "year")?.value ?? "";
  return `${day} ${mon}, ${yr}`.trim();
}

function ymd(d: any): string | null {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const da = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

function isAmountMatch(
  a: number | null | undefined,
  b: number | null | undefined,
  eps = 0.01
) {
  if (a == null || b == null) return false;
  return Math.abs(a - b) <= eps;
}

function MatchPill({ ok }: { ok: boolean }) {
  return ok ? (
    <span
      className={cn(
        "inline-flex items-center justify-center h-5 px-2 rounded-md",
        "border border-emerald-200 bg-emerald-50 text-emerald-800",
        "text-[10px] font-black tracking-wide"
      )}
    >
      Match
    </span>
  ) : (
    <span className="text-slate-300 text-[12px] font-semibold">—</span>
  );
}

function fmtMoneyWithCcy(amount: number | null | undefined, ccy?: string | null) {
  if (amount == null) return "—";
  const v = fmtMoney(amount);
  const c = (ccy ?? "").trim();
  return c ? `${c} ${v}` : v;
}

const extractRefTokens = (s?: string | null): string[] => {
  if (!s) return [];
  const up = String(s).toUpperCase().replace(/[|,;/]+/g, " ");
  const hits = up.match(/[A-Z0-9]+(?:-[A-Z0-9]+)+|[A-Z0-9]{6,32}/g) ?? [];
  return Array.from(new Set(hits.filter((tok) => /\d/.test(tok))));
};

const getRefOverlapTokens = (a?: string | null, b?: string | null): string[] => {
  const A = extractRefTokens(a);
  const B = extractRefTokens(b);
  if (!A.length || !B.length) return [];

  const setB = new Set(B);
  return A.filter((t) => setB.has(t));
};

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function HighlightedReference({
  value,
  matchedTokens,
}: {
  value?: string | null;
  matchedTokens: string[];
}) {
  if (!value) return <span className="text-slate-400">—</span>;

  if (!matchedTokens.length) {
    return <span className="break-all">{value}</span>;
  }

  const uniqueTokens = Array.from(new Set(matchedTokens)).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`(${uniqueTokens.map(escapeRegExp).join("|")})`, "gi");
  const parts = value.split(pattern);

  return (
    <span className="break-all leading-6">
      {parts.map((part, idx) => {
        const isMatch = uniqueTokens.some((t) => t.toUpperCase() === part.toUpperCase());

        if (!isMatch) {
          return <React.Fragment key={idx}>{part}</React.Fragment>;
        }

        return (
          <span
            key={idx}
            className={cn(
              "inline-flex items-center rounded-md px-1.5 py-0.5 mx-[1px]",
              "bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200",
              "font-bold"
            )}
          >
            {part}
          </span>
        );
      })}
    </span>
  );
}

function PairHeader({
  leftTitle,
  rightTitle,
  leftBadge,
  rightBadge,
  leftLabel = "Bank transaction",
  rightLabel = "Matched expense",
}: {
  leftTitle: React.ReactNode;
  rightTitle: React.ReactNode;
  leftBadge?: React.ReactNode;
  rightBadge?: React.ReactNode;
  leftLabel?: string;
  rightLabel?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2">
        <div className="px-3 py-2 bg-slate-50/70">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-wide text-slate-500">
                {leftLabel}
              </div>
              <div className="mt-1 text-[12px] font-bold text-slate-900 truncate">
                {leftTitle}
              </div>
            </div>
            {leftBadge ? <div className="shrink-0">{leftBadge}</div> : null}
          </div>
        </div>

        <div className="px-3 py-2 border-t sm:border-t-0 sm:border-l border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-wide text-slate-500">
                {rightLabel}
              </div>
              <div className="mt-1 text-[12px] font-bold text-slate-900 truncate">
                {rightTitle}
              </div>
            </div>
            {rightBadge ? <div className="shrink-0">{rightBadge}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function CompareTable({
  txAmount,
  expAmount,
  txCcy,
  expCcy,
  txDate,
  expDate,
  txRef,
  expRef,
}: {
  txAmount: number | null | undefined;
  expAmount: number | null | undefined;
  txCcy?: string | null;
  expCcy?: string | null;
  txDate: any;
  expDate: any;
  txRef: string;
  expRef: string;
}) {
  const amountOk = isAmountMatch(txAmount, expAmount);
  const dateOk = (ymd(txDate) != null && ymd(txDate) === ymd(expDate)) || false;

  const overlapTokens = getRefOverlapTokens(expRef, txRef);
  const refOk = overlapTokens.length > 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <table className="w-full table-fixed">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-wide w-[120px]">
              Field
            </th>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-wide">
              Bank
            </th>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-wide">
              Expense
            </th>
            <th className="px-3 py-2 text-center text-[10px] font-black text-slate-500 tracking-wide w-[90px]">
              Match
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100">
          <tr className="align-top">
            <td className="px-3 py-2 text-[10px] font-black tracking-wide text-slate-500">
              Amount
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900 tabular-nums">
              {fmtMoneyWithCcy(txAmount, txCcy)}
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900 tabular-nums">
              {fmtMoneyWithCcy(expAmount, expCcy)}
            </td>
            <td className="px-3 py-2 text-center">
              <MatchPill ok={amountOk} />
            </td>
          </tr>

          <tr className="align-top">
            <td className="px-3 py-2 text-[10px] font-black tracking-wide text-slate-500">
              Date
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900 tabular-nums">
              {fmtShortDate(txDate)}
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900 tabular-nums">
              {fmtShortDate(expDate)}
            </td>
            <td className="px-3 py-2 text-center">
              <MatchPill ok={dateOk} />
            </td>
          </tr>

          <tr className="align-top">
            <td className="px-3 py-2 text-[10px] font-black tracking-wide text-slate-500">
              Reference ID
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900">
              <HighlightedReference value={txRef} matchedTokens={overlapTokens} />
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900">
              <HighlightedReference value={expRef} matchedTokens={overlapTokens} />
            </td>
            <td className="px-3 py-2 text-center">
              <MatchPill ok={refOk} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ExtrasRow({
  expNet,
  txFee,
}: {
  expNet: number | null | undefined;
  txFee: number | null | undefined;
}) {
  if (expNet == null && txFee == null) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600">
        {txFee != null ? (
          <div>
            Fee{" "}
            <span className="font-semibold text-slate-900 tabular-nums">
              {fmtMoney(txFee)}
            </span>
          </div>
        ) : null}

        {expNet != null ? (
          <div>
            Net{" "}
            <span className="font-semibold text-slate-900 tabular-nums">
              {fmtMoney(expNet)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function TabMatchedBank({
  isLoading,
  error,
  matchedBank,
  pageMatchedBank,
  setPageMatchedBank,
  totalMB,
  accounts = [],
}: {
  isLoading: boolean;
  error: unknown;
  matchedBank: any[];
  pageMatchedBank: number;
  setPageMatchedBank: (p: number) => void;
  totalMB: number;
  accounts?: BankAccount[];
}) {
  const mbItems = React.useMemo(
    () =>
      matchedBank.slice(
        (pageMatchedBank - 1) * PAGE_SIZE,
        pageMatchedBank * PAGE_SIZE
      ),
    [matchedBank, pageMatchedBank]
  );

  const mbRange = rangeLabel(pageMatchedBank, matchedBank.length);

  const accountName = React.useCallback(
    (id?: string | null) =>
      (id && accounts.find((a) => String(a.id) === String(id))?.name) || id || "—",
    [accounts]
  );

  return (
    <TabsContent value="matched-bank" className="p-4 pt-4 space-y-2">
      <div className="text-[11px] text-slate-600">
        Bank transactions matched with paid expenses or internal transfers.
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <LoadingBlock />
        </div>
      ) : error ? (
        <ErrorBlock text="Failed to load matching data." />
      ) : matchedBank.length === 0 ? (
        <EmptyBlock text="No matched bank transactions in this range." />
      ) : (
        <TableShell
          page={pageMatchedBank}
          setPage={setPageMatchedBank}
          totalPages={totalMB}
          showing={mbRange}
          total={matchedBank.length}
          keyPrefix="mb"
        >
          <div className="space-y-2">
            {mbItems.map((row: any, idx: number) => {
              const globalRowNo = (pageMatchedBank - 1) * PAGE_SIZE + idx + 1;

              const t = row.tx;
              const m = row.match;
              const isTransfer = m?.type === "transfer";

              const whyText =
                Array.isArray(m.reasons) && m.reasons.length
                  ? m.reasons
                      .filter((x: string) => x !== "auto_exact")
                      .slice(0, 3)
                      .join(" · ")
                  : "";

              const txAmount = t.transaction_amount;
              const txCcy = t.transaction_amount_currency ?? "";
              const txRef = t.transaction_id_reference ?? "";

              let rightTitle: React.ReactNode;
              let rightLabel = "Matched expense";
              let compareNode: React.ReactNode;
              let extrasNode: React.ReactNode = null;

              if (isTransfer) {
                const tr = m.transfer;
                const counterpartyId =
                  m.side === "from" ? tr.to_account_id : tr.from_account_id;
                const counterpartyName = accountName(counterpartyId);
                const transferAmount =
                  m.side === "from" ? tr.from_amount : tr.to_amount;
                const transferCcy =
                  m.side === "from" ? tr.from_currency : tr.to_currency;
                const direction = m.side === "from" ? "To" : "From";

                rightTitle = `${direction} ${counterpartyName}`;
                rightLabel = "Internal transfer";

                compareNode = (
                  <CompareTable
                    txAmount={txAmount}
                    expAmount={Number(transferAmount) || 0}
                    txCcy={txCcy}
                    expCcy={transferCcy}
                    txDate={t.transaction_date}
                    expDate={tr.transfer_date}
                    txRef={txRef}
                    expRef={tr.reference_number ?? ""}
                  />
                );

                extrasNode = (
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600">
                      <div>
                        From{" "}
                        <span className="font-semibold text-slate-900">
                          {accountName(tr.from_account_id)}
                        </span>
                      </div>
                      <div>
                        To{" "}
                        <span className="font-semibold text-slate-900">
                          {accountName(tr.to_account_id)}
                        </span>
                      </div>
                      {tr.description ? (
                        <div className="truncate max-w-[260px]" title={tr.description}>
                          {tr.description}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              } else {
                const e = m.expense;
                const paidDate = e.payment_date ?? e.expense_date;
                const expTotal = e.total_amount ?? (e as any).expense_total;
                const expCcy = e.currency ?? "";
                const expRef = e.payment_reference ?? "";

                rightTitle = e.vendor_name ?? "Vendor";
                rightLabel = "Matched expense";

                compareNode = (
                  <CompareTable
                    txAmount={txAmount}
                    expAmount={expTotal}
                    txCcy={txCcy}
                    expCcy={expCcy}
                    txDate={t.transaction_date}
                    expDate={paidDate}
                    txRef={txRef}
                    expRef={expRef}
                  />
                );

                extrasNode = (
                  <ExtrasRow expNet={e.net_payment} txFee={t.transaction_fee} />
                );
              }

              const rowKey = isTransfer
                ? `${t.id}-T-${m.transfer_id}-${m.side}`
                : `${t.id}-${m.expense_id}`;

              return (
                <div
                  key={rowKey}
                  className="rounded-lg border border-slate-200 bg-white p-2.5 sm:p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-baseline gap-3">
                      <div className="text-[11px] font-semibold text-slate-500 tabular-nums shrink-0">
                        #{globalRowNo}
                      </div>

                      {whyText ? (
                        <div className="min-w-0 text-[11px] text-slate-700 truncate">
                          <span className="font-black text-slate-900">Why:</span>{" "}
                          <span className="font-semibold">{whyText}</span>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <MatchKindBadge kind={m.kind} />
                      <ScoreBadge score={m.score} />
                    </div>
                  </div>

                  <div className="mt-2">
                    <PairHeader
                      leftTitle={t.description_details ?? "Bank transaction"}
                      rightTitle={rightTitle}
                      leftLabel="Bank transaction"
                      rightLabel={rightLabel}
                    />
                  </div>

                  <div className="mt-2">{compareNode}</div>

                  {extrasNode ? <div className="mt-2">{extrasNode}</div> : null}
                </div>
              );
            })}
          </div>
        </TableShell>
      )}
    </TabsContent>
  );
}